/**
 * AutomationTrigger
 *
 * A platform-independent trigger model.
 * Does not reference Blockly or Home Assistant.
 *
 * A trigger describes WHEN an automation fires:
 *   source='widget' + event='down'   — a widget interaction
 *   source='timer'                   — a time-based trigger (future)
 *   source='mqtt'                    — an MQTT message (future)
 *   source='state' + entityId        — an entity state change (optionally
 *                                      `to`/`from` a state, or of `attribute`)
 *   source='homeassistant'           — an HA event (future)
 */
export class AutomationTrigger {
  /**
   * @param {object} config
   * @param {string} [config.id]
   * @param {'widget'|'timer'|'mqtt'|'state'|'homeassistant'} [config.source]
   * @param {string} [config.event]     — event name, e.g. 'down', 'up', 'changed'
   * @param {object|null} [config.filter]  — optional filter; e.g. { val: 0 }
   * @param {string|null} [config.widgetId] — e.g. 'p1b1'; required when source='widget'
   * @param {string} [config.entityId]  — the entity whose state change fires; source='state'
   * @param {string} [config.to]        — only a change to this state (source='state')
   * @param {string} [config.from]      — only a change from this state (source='state')
   * @param {string} [config.attribute] — a change of this attribute instead of the state
   */
  constructor({ id, source = 'widget', event = '', filter = null, widgetId = null, entityId = '', to = '', from = '', attribute = '' } = {}) {
    this.id = id ?? crypto.randomUUID();
    this.source   = source;
    this.event    = event;
    this.filter   = filter;
    this.widgetId = widgetId;
    this.entityId  = entityId;
    this.to        = to;
    this.from      = from;
    this.attribute = attribute;
  }

  toJSON() {
    const out = { id: this.id, source: this.source, event: this.event };
    if (this.filter   !== null) out.filter   = this.filter;
    if (this.widgetId !== null) out.widgetId = this.widgetId;
    for (const key of ['entityId', 'to', 'from', 'attribute']) if (this[key]) out[key] = this[key];
    return out;
  }

  static fromJSON(data) {
    if (!data) return null;
    return new AutomationTrigger(data);
  }
}
